import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type { PatientMedication } from '@/types/patient-chart-api';

/**
 * Patient medications list — Chart > Medications tab (PROD-737 PR A).
 *
 * Wire (Flutter `patient_chart_data_source_impl.dart:664`):
 *
 *   GET /api/Patients/GetPatientMedicationDetails
 *       ?PatientId={id}&IsActive={true|false}
 *       → PatientMedication[]
 *
 * The endpoint partitions by `IsActive` — Flutter fires it twice
 * (once for `true`, once for `false`) and renders two sections:
 * Active (top) + Past (bottom). Same partition in React via two
 * parallel `useQuery` calls.
 */

export function usePatientMedications(
  patientId: string | null | undefined,
  isActive: boolean,
) {
  return useQuery({
    queryKey: ['patient-medications', patientId, isActive],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({
        PatientId: patientId ?? '',
        IsActive: String(isActive),
      });
      return get<PatientMedication[]>(
        `/api/Patients/GetPatientMedicationDetails?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
